import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  PlayIcon,
  CalendarDaysIcon,
  ClockIcon,
  Cog6ToothIcon,
  CloudIcon,
} from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { formatDateTime, formatTradingDay, statusBadge, formatBytes } from './dailyDataUtils'
import ImportConfirmModal from './ImportConfirmModal'
import BlobFilePreviewModal from './BlobFilePreviewModal'
import BatchIngestProgressModal from './BatchIngestProgressModal'

export default function DimPatronFeed() {
  const [data, setData] = useState(null)
  const [azureSources, setAzureSources] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadingAzure, setLoadingAzure] = useState(false)
  const [running, setRunning] = useState(false)
  const [message, setMessage] = useState(null)
  const [tradingDay, setTradingDay] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [expandedFolder, setExpandedFolder] = useState(null)
  const [azureNav, setAzureNav] = useState({ year: null, month: null })
  const [confirmOpts, setConfirmOpts] = useState(null)
  const [previewBlobPath, setPreviewBlobPath] = useState(null)
  const [batchIngest, setBatchIngest] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl('/api/daily-data/patron?days=14'))
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to load DimPatron status')
      setData(json.data)
      setTradingDay(prev => prev || json.data?.trading_day_yesterday || json.data?.trading_day_today || '')
      setTemplateId(prev => prev || (json.data?.default_template_id ? String(json.data.default_template_id) : ''))
      if (json.data?.azure_sources) setAzureSources(json.data.azure_sources)
    } catch (e) {
      setMessage({ type: 'error', text: e.message })
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchAzureSources = async ({ year, month } = {}) => {
    const params = new URLSearchParams()
    if (year) params.set('year', year)
    if (month) params.set('month', month)
    const qs = params.toString()
    const res = await authFetch(getLocalApiUrl(`/api/daily-data/patron/sources${qs ? `?${qs}` : ''}`))
    const json = await res.json()
    if (!json.success) throw new Error(json.error || 'Failed to list Azure extracts')
    return json.data
  }

  const loadAzure = useCallback(async (nav = {}) => {
    setLoadingAzure(true)
    try {
      let year = nav.year ?? null
      let month = nav.month ?? null

      if (nav.level === 'root') {
        year = null
        month = null
      } else if (nav.level === 'year') {
        month = null
      }

      // First visit: open newest year + month so day folders match blob layout
      if (!nav.explicit && !year) {
        const root = await fetchAzureSources({})
        if (root.years?.length) {
          year = root.years[0].year
        }
      }
      if (!nav.explicit && year && !month) {
        const monthsView = await fetchAzureSources({ year })
        if (monthsView.months?.length) {
          month = monthsView.months[0].month_key
        }
      }

      const data = year && month
        ? await fetchAzureSources({ year, month })
        : year
          ? await fetchAzureSources({ year })
          : await fetchAzureSources({})

      setAzureSources(data)
      setAzureNav({
        year: data.year || year || null,
        month: data.month || month || null,
      })
    } catch (e) {
      setMessage({ type: 'error', text: e.message })
    } finally {
      setLoadingAzure(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (data?.config?.source_type === 'azure_blob' && !azureSources) {
      loadAzure()
    }
  }, [data, azureSources, loadAzure])

  const requestImport = (opts = {}) => {
    const day = opts.tradingDay ?? tradingDay
    if (!opts.blobPath && !day && opts.tradingDay !== 'yesterday') {
      setMessage({ type: 'error', text: 'Select a trading day or pick a file from Azure' })
      return
    }
    setConfirmOpts(opts)
  }

  const runImport = async (opts = {}) => {
    const day = opts.tradingDay ?? tradingDay
    setRunning(true)
    setMessage(null)
    try {
      const body = {}
      if (opts.tradingDay === 'yesterday') body.trading_day = 'yesterday'
      else if (day) body.trading_day = day
      if (templateId) body.template_id = parseInt(templateId, 10)
      if (opts.blobPath) body.blob_path = opts.blobPath
      const res = await authFetch(getLocalApiUrl('/api/daily-data/patron/run'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Import failed')
      const d = json.data
      const summary = d.import_strategy === 'insert_only'
        ? `Import complete — ${d.imported} new patrons added, ${d.skipped_existing ?? 0} row(s) matched existing (${d.skipped_existing_unique ?? '?'} unique member ID(s)), ${d.skipped_filtered ?? 0} filtered, ${d.errors} errors (${d.duration_ms}ms)`
        : d.import_strategy === 'sync_existing'
          ? `Import complete — ${d.imported} new, ${d.updated ?? 0} synced (membership/contact), ${d.skipped_filtered ?? 0} filtered, ${d.errors} errors (${d.duration_ms}ms)`
          : `Import complete — ${d.imported} imported, ${d.updated} updated, ${d.errors} errors (${d.duration_ms}ms)`
      let type = 'success'
      if (d.errors > 0 && d.imported === 0) type = 'error'
      else if (d.errors > 0) type = 'warning'
      setMessage({
        type,
        summary,
        errorDetails: d.error_details?.length ? d.error_details : (d.error_message ? d.error_message.split('\n') : []),
      })
      setConfirmOpts(null)
      await load()
      if (data?.config?.source_type === 'azure_blob') await loadAzure()
    } catch (e) {
      setMessage({ type: 'error', text: e.message })
    } finally {
      setRunning(false)
    }
  }

  const handleImportConfirm = () => {
    if (confirmOpts?.importAllBatches) {
      const dayFolders = azureSources?.days?.length
        ? azureSources.days
        : (azureSources?.sources || [])
      const folder = dayFolders.find(
        s => s.folder_path === confirmOpts.folderPath
          || s.trading_day === confirmOpts.tradingDay,
      )
      if (folder?.files?.length) {
        setBatchIngest({
          files: folder.files,
          tradingDay: folder.trading_day,
          folderName: folder.folder_name || folder.folder_path,
        })
        setConfirmOpts(null)
        return
      }
    }
    runImport(confirmOpts)
  }

  const handleBatchComplete = async (totals) => {
    const { imported, updated, skipped, errors, files_done, files_total, stopped } = totals
    let type = 'success'
    if (errors > 0 && imported === 0 && !(updated > 0)) type = 'error'
    else if (errors > 0 || stopped) type = 'warning'
    const syncPart = updated != null ? `${updated} synced, ` : ''
    setMessage({
      type,
      summary: stopped
        ? `Batch stopped — ${files_done}/${files_total} files processed: ${imported} new, ${syncPart}${skipped} skipped rows, ${errors} errors`
        : `Batch complete — ${files_total} files: ${imported} new, ${syncPart}${skipped} skipped rows, ${errors} errors`,
    })
    setBatchIngest(null)
    await load()
    if (data?.config?.source_type === 'azure_blob') await loadAzure()
  }

  const isAzure = data?.config?.source_type === 'azure_blob'
  const cfg = data?.config
  const batchRunning = !!batchIngest
  const importBusy = running || batchRunning
  const azureDayFolders = azureSources?.days?.length
    ? azureSources.days
    : (azureSources?.sources || [])

  const navigateAzure = (level, year = null, month = null) => {
    setExpandedFolder(null)
    loadAzure({ explicit: true, level, year, month })
  }

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <Link to="/monitor-safe/daily-data" className="inline-flex items-center hover:text-gray-900">
          <ArrowLeftIcon className="w-4 h-4 mr-1" />
          Daily Data
        </Link>
        <span>/</span>
        <span className="font-medium text-gray-900">DimPatron</span>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">DimPatron</h1>
            <p className="text-gray-600 mt-1">Member directory sync from vendor extract files.</p>
            <p className="text-xs text-gray-500 mt-2">
              Source: <span className="font-medium">{cfg?.source_type || 'local'}</span>
              {isAzure && azureSources?.account_name && (
                <> — {azureSources.account_name}/{azureSources.container}/{azureSources.prefix}</>
              )}
              {!isAzure && cfg?.import_directory && <> — {cfg.import_directory}</>}
              {' · '}
              <Link to="/profile-safe/search" className="text-primary-600 hover:underline">Search patrons</Link>
              {' '}in Profile Safe (member ID e.g. WT-369)
            </p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={load} className="inline-flex items-center px-3 py-2 border rounded-md text-sm hover:bg-gray-50">
              <ArrowPathIcon className="w-4 h-4 mr-1" />
              Refresh
            </button>
            <Link to="/monitor-safe/daily-data/settings" className="inline-flex items-center px-3 py-2 border rounded-md text-sm hover:bg-gray-50">
              <Cog6ToothIcon className="w-4 h-4 mr-1" />
              Settings
            </Link>
          </div>
        </div>
      </div>

      {/* Scheduled import */}
      <div className="bg-white shadow rounded-lg p-6 border-l-4 border-primary-500">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <ClockIcon className="w-5 h-5 text-primary-600" />
          Scheduled import
        </h2>
        <p className="text-sm text-gray-600 mt-2">{data?.schedule_description || 'Configure in Data Imports'}</p>
        {cfg?.enabled ? (
          <p className="text-xs text-green-700 mt-2">
            Next cron run will import trading day:{' '}
            <strong>{formatTradingDay(data?.scheduled_trading_day)}</strong>
            {' '}(offset {cfg?.trading_day_offset ?? 1} day(s))
          </p>
        ) : (
          <p className="text-xs text-amber-700 mt-2">
            Scheduler disabled — use manual ingest below, or enable in{' '}
            <Link to="/monitor-safe/daily-data/settings" className="underline">Daily Data settings</Link>.
            {' '}Example for 5pm daily yesterday: cron <code className="bg-gray-100 px-1">0 17 * * *</code>
          </p>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={importBusy}
            onClick={() => requestImport({ tradingDay: 'yesterday' })}
            className="inline-flex items-center px-3 py-1.5 bg-primary-600 text-white rounded-md text-sm hover:bg-primary-700 disabled:opacity-50"
          >
            <PlayIcon className="w-4 h-4 mr-1" />
            Import yesterday now
          </button>
        </div>
      </div>

      {message && (
        <div className={`rounded-lg p-4 text-sm border ${
          message.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : message.type === 'warning'
              ? 'bg-amber-50 border-amber-200 text-amber-900'
              : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <p>{message.summary || message.text}</p>
          {(message.errorDetails?.length > 0) && (
            <div className="mt-3 pt-3 border-t border-current/20">
              <p className="font-medium mb-1">Error details</p>
              <ul className="list-disc list-inside space-y-0.5 font-mono text-xs opacity-90 max-h-40 overflow-y-auto">
                {message.errorDetails.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Azure TPDataExtracts — year / month / day drill-down */}
      {(isAzure || azureSources) && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <CloudIcon className="w-5 h-5 text-sky-600" />
              Available in Azure (TPDataExtracts)
            </h2>
            <button
              type="button"
              onClick={() => loadAzure({ explicit: true, year: azureNav.year, month: azureNav.month })}
              disabled={loadingAzure}
              className="text-sm text-primary-600 hover:underline disabled:opacity-50"
            >
              {loadingAzure ? 'Loading…' : 'Refresh list'}
            </button>
          </div>

          {!azureSources?.configured && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
              {azureSources?.error || 'Azure not configured. Set source to Azure blob in Data Imports and add sid_storage_conn_string on a venue.'}
            </p>
          )}

          {azureSources?.configured && (
            <>
              <nav className="flex flex-wrap items-center gap-1 text-sm text-gray-600 mb-4" aria-label="Blob path">
                <button
                  type="button"
                  onClick={() => navigateAzure('root')}
                  className="font-mono text-primary-600 hover:underline"
                >
                  TPDataExtracts
                </button>
                {azureNav.year && (
                  <>
                    <span>/</span>
                    <button
                      type="button"
                      onClick={() => navigateAzure('year', azureNav.year)}
                      className="font-mono text-primary-600 hover:underline"
                    >
                      {azureNav.year}
                    </button>
                  </>
                )}
                {azureNav.year && azureNav.month && (
                  <>
                    <span>/</span>
                    <span className="font-mono text-gray-900">{azureNav.month}</span>
                    {azureSources.month_label && (
                      <span className="text-gray-500 ml-1">({azureSources.month_label})</span>
                    )}
                  </>
                )}
              </nav>

              {azureSources.level === 'years' && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {(azureSources.years || []).map(y => (
                    <button
                      key={y.year}
                      type="button"
                      onClick={() => navigateAzure('year', y.year)}
                      className="px-4 py-3 border rounded-lg hover:bg-gray-50 text-left"
                    >
                      <div className="text-lg font-semibold text-gray-900">{y.year}</div>
                      <div className="text-xs text-gray-500 font-mono truncate">{y.path}</div>
                    </button>
                  ))}
                  {!azureSources.years?.length && (
                    <p className="col-span-full text-sm text-gray-500 py-4 text-center">No year folders under {azureSources.prefix}</p>
                  )}
                </div>
              )}

              {azureSources.level === 'months' && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {(azureSources.months || []).map(m => (
                    <button
                      key={m.month_key}
                      type="button"
                      onClick={() => navigateAzure('month', azureSources.year, m.month_key)}
                      className="px-4 py-3 border rounded-lg hover:bg-gray-50 text-left"
                    >
                      <div className="font-semibold text-gray-900">{m.label}</div>
                      <div className="text-xs text-gray-500 font-mono">{m.month_key}</div>
                    </button>
                  ))}
                  {!azureSources.months?.length && (
                    <p className="col-span-full text-sm text-gray-500 py-4 text-center">No month folders for {azureSources.year}</p>
                  )}
                </div>
              )}

              {azureSources.level === 'days' && (
                <>
                  <p className="text-xs text-gray-500 mb-3">
                    {azureDayFolders.length} day folder(s) in blob storage
                    {' · '}
                    {azureDayFolders.filter(d => d.has_dimpatron).length} with DimPatron files
                  </p>

                  {azureDayFolders.length === 0 && (
                    <p className="text-sm text-gray-500 py-4 text-center">No day folders in this month</p>
                  )}

                  <div className="space-y-2">
                    {azureDayFolders.map(folder => (
                      <div key={folder.folder_path} className="border rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setExpandedFolder(expandedFolder === folder.folder_path ? null : folder.folder_path)}
                          className="w-full flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left"
                        >
                          <div>
                            <span className="font-medium text-gray-900">
                              {folder.trading_day ? formatTradingDay(folder.trading_day) : folder.folder_name}
                            </span>
                            {folder.folder_name && folder.trading_day && (
                              <span className="ml-2 text-sm font-mono text-gray-500">{folder.folder_name}</span>
                            )}
                            {!folder.has_dimpatron && (
                              <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">No DimPatron</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-500">
                              {folder.file_count || 0} DimPatron file(s)
                              {folder.total_bytes ? ` · ${formatBytes(folder.total_bytes)}` : ''}
                            </span>
                            {folder.has_dimpatron && (
                              <button
                                type="button"
                                disabled={importBusy}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  requestImport({
                                    tradingDay: folder.trading_day,
                                    folderPath: folder.folder_path,
                                    importAllBatches: true,
                                  })
                                }}
                                className="px-2 py-1 bg-primary-600 text-white rounded text-xs hover:bg-primary-700 disabled:opacity-50"
                              >
                                Ingest all batches
                              </button>
                            )}
                          </div>
                        </button>
                        {expandedFolder === folder.folder_path && folder.files?.length > 0 && (
                          <ul className="divide-y divide-gray-100">
                            {folder.files.map(file => (
                              <li key={file.blob_path} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm">
                                <span className="font-mono text-gray-800 truncate max-w-xl" title={file.blob_path}>{file.name}</span>
                                <span className="text-gray-500">{formatBytes(file.size)}</span>
                                <button
                                  type="button"
                                  onClick={() => setPreviewBlobPath(file.blob_path)}
                                  className="text-gray-600 hover:underline text-sm"
                                >
                                  Preview
                                </button>
                                <button
                                  type="button"
                                  disabled={importBusy}
                                  onClick={() => requestImport({ blobPath: file.blob_path, tradingDay: folder.trading_day })}
                                  className="text-primary-600 hover:underline font-medium disabled:opacity-50"
                                >
                                  Ingest
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        {expandedFolder === folder.folder_path && !folder.files?.length && (
                          <p className="px-4 py-3 text-sm text-gray-500">Folder exists in blob storage but has no DimPatron .txt/.csv files yet.</p>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Manual by trading day */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Import by trading day</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <CalendarDaysIcon className="w-4 h-4 inline mr-1" />
              Trading day
            </label>
            <input
              type="date"
              value={tradingDay}
              onChange={(e) => setTradingDay(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mapping template</label>
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">Default template</option>
              {(data?.templates || []).map(t => (
                <option key={t.id} value={t.id}>{t.template_name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              <Link to="/monitor-safe/daily-data/settings" className="text-primary-600 hover:underline">Settings</Link>
              {' · '}
              <Link to="/admin/group-config" className="text-primary-600 hover:underline">Edit mappings</Link>
            </p>
          </div>
        </div>
        <div className="mt-4">
          <button
            type="button"
            disabled={importBusy}
            onClick={() => requestImport()}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
          >
            <PlayIcon className="w-4 h-4 mr-1" />
            {running ? 'Running…' : 'Start'}
          </button>
        </div>
      </div>

      {/* Trading days table */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Recent trading days</h2>
        {isAzure && (
          <p className="text-sm text-gray-500 mb-4">
            Calendar dates for import history. <strong>Start</strong> only works when a row shows files in Azure
            ({data?.azure_available_days?.length || 0} day{(data?.azure_available_days?.length || 0) === 1 ? '' : 's'} listed above).
            Otherwise use <strong>Ingest</strong> on a specific blob file.
          </p>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Trading day</th>
                {isAzure && (
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">In Azure</th>
                )}
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Run at</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Imported</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Skipped</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Errors</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data?.trading_days || []).map(row => (
                <tr key={row.trading_day} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-sm font-medium text-gray-900">{formatTradingDay(row.trading_day)}</td>
                  {isAzure && (
                    <td className="px-3 py-2 text-sm text-gray-600">
                      {row.azure_blob_available ? (
                        <span className="text-green-700" title={row.azure_folder}>
                          {row.azure_file_count} file{row.azure_file_count === 1 ? '' : 's'}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2">{statusBadge(row.status)}</td>
                  <td className="px-3 py-2 text-sm text-gray-600">{formatDateTime(row.run_at)}</td>
                  <td className="px-3 py-2 text-sm text-right">{row.imported ?? '—'}</td>
                  <td className="px-3 py-2 text-sm text-right text-gray-600" title={row.skipped != null ? `${row.skipped} rows not added` : undefined}>
                    {row.skipped ?? '—'}
                  </td>
                  <td className={`px-3 py-2 text-sm text-right ${row.errors > 0 ? 'text-red-600 font-medium' : 'text-gray-700'}`}>
                    {row.errors ?? '—'}
                  </td>
                  <td className="px-3 py-2">
                    {row.status !== 'ingested' && (isAzure ? row.azure_blob_available : true) && (
                      <button
                        type="button"
                        disabled={importBusy}
                        onClick={() => requestImport({ tradingDay: row.trading_day })}
                        className="text-sm text-primary-600 hover:text-primary-800 font-medium disabled:opacity-50"
                      >
                        Start
                      </button>
                    )}
                    {row.status !== 'ingested' && isAzure && !row.azure_blob_available && (
                      <span className="text-xs text-gray-400">No blob</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Import run log</h2>
        {(data?.recent_runs || []).length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">No import runs yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Run at</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Trading day</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Imported</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Skipped</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Errors</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.recent_runs.map(run => (
                  <React.Fragment key={run.id}>
                  <tr>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(run.run_at)}</td>
                    <td className="px-3 py-2">{run.trading_day ? formatTradingDay(String(run.trading_day).slice(0, 10)) : '—'}</td>
                    <td className="px-3 py-2">{statusBadge(run.status === 'success' ? 'ingested' : run.status)}</td>
                    <td className="px-3 py-2 text-right">{run.imported ?? 0}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{run.skipped ?? 0}</td>
                    <td className="px-3 py-2 text-right text-red-700">
                      {run.errors > 0 ? (
                        <span title={run.error_message || undefined}>{run.errors}</span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-500 max-w-xs truncate" title={run.file_path}>{run.file_path || '—'}</td>
                  </tr>
                  {run.errors > 0 && run.error_message && (
                    <tr className="bg-red-50/50">
                      <td colSpan={6} className="px-3 py-2 text-xs font-mono text-red-800 whitespace-pre-wrap">
                        {run.error_message}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ImportConfirmModal
        open={!!confirmOpts}
        importOpts={confirmOpts}
        templateId={templateId}
        confirming={running}
        onClose={() => { if (!running) setConfirmOpts(null) }}
        onConfirm={handleImportConfirm}
      />

      <BatchIngestProgressModal
        open={!!batchIngest}
        files={batchIngest?.files || []}
        tradingDay={batchIngest?.tradingDay}
        folderName={batchIngest?.folderName}
        templateId={templateId}
        onClose={() => setBatchIngest(null)}
        onComplete={handleBatchComplete}
      />

      <BlobFilePreviewModal
        open={!!previewBlobPath}
        blobPath={previewBlobPath}
        onClose={() => setPreviewBlobPath(null)}
      />
    </div>
  )
}
